import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { addImpulse } from './prices';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * 72-hour Saudi yacht trip mini-arc.
 *
 * Triggered when player accepts the Saudi prince's yacht invite. Three
 * "moments come up during the trip" delivered via Phone CTAs. The arc has
 * persistent consequences: $80K cash bump, FBI heat, marriage hit, BOOM
 * impulse from "discussions held in international waters."
 */

interface Moment {
  text: string;
  options: { label: string; effect: () => void }[];
}

const MOMENTS: Moment[] = [
  {
    text: 'PRINCE: "we should toast. to friendships."',
    options: [
      { label: 'drink it', effect: () => {
        useLife.getState().bumpSanity(-4);
        useMarkets.getState().addCash(20_000);
      }},
      { label: '"i don\'t drink, your highness."', effect: () => {
        usePower.getState().bumpInfluence(4);
      }},
      { label: 'pour it overboard discreetly', effect: () => {
        useLife.getState().bumpSanity(2);
      }},
    ],
  },
  {
    text: 'PRINCE: "we have certain... assets... we\'d like to move into U.S. defense names. you would be helpful."',
    options: [
      { label: '"of course. $BOOM, perhaps?"', effect: () => {
        useMarkets.getState().addCash(60_000);
        useLife.getState().bumpFbiHeat(15);
        addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: 0.08 });
      }},
      { label: '"i should consult my counsel."', effect: () => {
        useMarkets.getState().addCash(-3_000);
        usePower.getState().bumpInfluence(-2);
      }},
      { label: '"i\'m here to relax, your highness."', effect: () => {
        useLife.getState().bumpSanity(4);
      }},
    ],
  },
  {
    text: 'PRINCE: "your wife. she is well? we noticed you have not called her. our staff can arrange a... distraction."',
    options: [
      { label: '"i\'ll call her now, thank you."', effect: () => {
        useLife.getState().bumpRelationship('wife', 8);
        useLife.getState().bumpSanity(2);
      }},
      { label: '"that won\'t be necessary."', effect: () => {
        useLife.getState().bumpRelationship('wife', -8);
      }},
      { label: '"... what kind of distraction?"', effect: () => {
        useLife.getState().bumpRelationship('wife', -25);
        useLife.getState().bumpSanity(-10);
        useLife.getState().bumpFbiHeat(8);
      }},
    ],
  },
];

export function startYachtTrip(): void {
  useMeta.getState().recordLore('arc-yacht-trip', 'Saudi Yacht Trip');
  useCinematic.getState().fire({
    id: `yacht-${Date.now()}`,
    type: 'breaking',
    headline: 'INTERNATIONAL WATERS.',
    subhead: 'a 240-foot yacht. mediterranean sun. nobody asking questions. you have arrived where every degenerate trader thinks he wants to end up.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < MOMENTS.length; i++) {
    const m = MOMENTS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = m.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `yacht-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < m.options.length; j++) {
        const id = `yacht-q${i}-${ids[j]}`;
        const opt = m.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'saudi-prince',
        fromNpc: 'saudi-prince',
        text: m.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `yacht: moment ${i + 1}/3` });
      sounds.notifyAlert();
    }, 5_000 + i * 35_000);
  }

  // After-trip fallout
  setTimeout(() => {
    useXClone.getState().receivePost({
      authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
      text: 'BREAKING: trader spotted on a Saudi prince\'s yacht in international waters. State Department declines to comment. State Department\'s spokesperson declines further comment on the prior decline.',
      likes: 12200, reposts: 4400,
    });
    usePower.getState().bumpNotoriety(15);
    useLife.getState().bumpFbiHeat(10);
  }, 5_000 + MOMENTS.length * 35_000 + 5_000);
}
